// Copyright (c) 2025 by Audere

import React, { useEffect, useState } from "react";
import { useLabelerConversations } from "../hooks/useLabelerConversations";
import Sidebar from "./Sidebar";
import MainPanel from "./MainPanel";
import {
  useLocation,
  useNavigate,
  useParams,
  useSearchParams,
} from "react-router-dom";
import { setLabeler } from "../features/slices/labelSlice";
import { useSelector } from "react-redux";
import { selectCurrentEmail } from "../features/slices/authSlice";
import { useAppDispatch } from "../features/store";
import { LabelerConversationData } from "../features/api/labelApiSlice";
import { useConversationForLabeler } from "../hooks/useConversationForLabeler";
import { conversationLimit } from "../constants/conversation";

const LabelerConversations: React.FC = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const { conversationUID, labelTypeID } = useParams();
  const dispatch = useAppDispatch();
  const [allConversations, setAllConversations] = useState<
    LabelerConversationData[]
  >([]);
  const [searchParams, setSearchParams] = useSearchParams();
  const pageParam = searchParams.get("page");
  const pageValue =
    pageParam && !isNaN(Number(pageParam)) && Number(pageParam) > 0
      ? Number(pageParam)
      : 1;
  const [offsetValue, setOffsetValue] = useState(
    (pageValue - 1) * conversationLimit
  );
  const [hasMore, setHasMore] = useState(true);

  const { conversations, offset, endOfList, isLoading, error } =
    useLabelerConversations(labelTypeID ?? "", offsetValue);
  const userEmail = useSelector(selectCurrentEmail);
  const conversation = allConversations.find(
    (c) => c.conversation_uid === conversationUID
  );
  const {
    conversation: conversationMessages,
    isLoading: convoIsLoading,
    error: convoError,
  } = useConversationForLabeler(
    conversationUID ?? "", labelTypeID ?? ""
  );

  const handleLoadMore = (value: "next" | "prev") => {
    if (!isLoading) {
      setAllConversations([]);
      let newOffset = offsetValue;
      if (value === "next") {
        newOffset = offsetValue + conversationLimit;
      } else {
        newOffset = Math.max(offsetValue - conversationLimit, 0);
      }
      const newPage = Math.floor(newOffset / conversationLimit) + 1;
      searchParams.set("page", String(newPage));
      setSearchParams(searchParams);
      setOffsetValue(newOffset);
      navigate({
        pathname: location.pathname,
        search: `?${searchParams.toString()}`,
      });
    }
  };

  useEffect(() => {
    if (!pageParam || isNaN(Number(pageParam)) || Number(pageParam) <= 0) {
      searchParams.set("page", "1");
      setSearchParams(searchParams);
      navigate(
        { pathname: location.pathname, search: `?${searchParams.toString()}` },
        { replace: true }
      );
    }
  }, [location.pathname, navigate, searchParams, setSearchParams, pageParam]);
  useEffect(() => {
    if (conversations.length > 0) {
      setAllConversations(conversations);
    }
  }, [conversations]);

  useEffect(() => {
    if (endOfList && offset > 0) {
      setHasMore(true);
    } else {
      setHasMore(!endOfList);
    }
  }, [endOfList, offset]);
  useEffect(() => {
    const foundLabeler =
      conversation?.labelers.find((labeler) => labeler.email === userEmail) ||
      conversationMessages?.labelers.find(
        (labeler) => labeler.email === userEmail
      );
    dispatch(setLabeler(foundLabeler));
  }, [
    conversation?.labelers,
    conversationMessages?.labelers,
    dispatch,
    userEmail,
  ]);

  return (
    <>
      <Sidebar
        isLoading={isLoading}
        error={error}
        conversations={allConversations}
        onLoadMore={handleLoadMore}
        hasMore={hasMore}
        endOfList={!!endOfList}
        conversationMessages={conversationMessages}
      />
      <MainPanel
        isLoading={isLoading}
        error={error}
        conversationMessages={conversationMessages}
        convoIsLoading={convoIsLoading}
        convoError={convoError}
      />
    </>
  );
};

export default LabelerConversations;
